/**
 * «Recibir recado»: the bearer scans the errand the grantor's phone shows and
 * keeps it, once it names this holder, carries the grantor's device signature
 * over a credential the UH signed, and is still alive.
 */
import { ATTR } from "../constants";
import { decodeAttributes, decodeCredential } from "../format";
import { startScanner } from "../scan";
import { checkStatus } from "../status";
import {
  correctedNow, loadCredential, loadStatusList, loadTrustStore, saveErrand,
} from "../storage";
import { escapeHTML, formatWhen, REASON_COPY, type RefusalReason } from "../ui";
import { verifyErrand } from "../verify";
import { scanOnce } from "./camera";
import type { Scanner } from "./scan";
import { ICON } from "../icons";

/** Where the holder's reading differs from the verifier's: here the person
 *  "presenting" is the one holding this phone. */
const RECEIVE_COPY: Partial<Record<RefusalReason, string>> = {
  "errand-malformed":
    "Este código no es un recado. Pide a quien te autoriza que abra «Crear recado» y te muestre el código que sale al final.",
  "errand-wrong-bearer": "Este recado es para otra persona: no lleva tu identificador.",
};

export async function renderErrandReceive(
  root: HTMLElement,
  onDone: () => void,
  scanner: Scanner = startScanner,
): Promise<() => void> {
  let stopCamera: () => void = () => {};
  const credential = await loadCredential();
  if (!credential) throw new Error("no credential on this device");
  const ownUid = decodeAttributes(decodeCredential(credential).attributesBytes).get(ATTR.ID);

  const scan = async () => {
    stopCamera = await scanOnce(
      root, "Escanea el recado que te muestra quien te autoriza",
      (payload) => void onScanned(payload), scanner,
    );
  };

  const onScanned = async (payload: Uint8Array) => {
    let reason: RefusalReason;
    try {
      const { now, synced } = await correctedNow();
      if (!synced) {
        reason = "clock-unsynced";
      } else {
        const statusList = await loadStatusList();
        const check = await verifyErrand(payload, {
          issuerPublicKeys: await loadTrustStore(),
          now,
          status: (index) => checkStatus(statusList, index),
          bearer: ownUid,
        });
        if (check.ok) {
          await saveErrand(payload, check.errand.expiresAt);
          const { grantorName, grantorRole, text, expiresAt } = check.errand;
          root.innerHTML = `<div class="result good"><div class="badge">${ICON.check}</div>
            <h1>Recado guardado</h1>
            <div class="errand-block"><p>${escapeHTML(grantorName)}, ${escapeHTML(grantorRole)}, te autorizó:
            <strong>${escapeHTML(text)}</strong></p>
            <p>Vence ${escapeHTML(formatWhen(expiresAt))}</p></div>
            <p>Lo encuentras en «Recados» y encima de tu código. Tócalo cuando tengas
            que presentarlo.</p>
            <div class="foot"><button id="done">Ver mi identidad</button></div></div>`;
          root.querySelector("#done")!.addEventListener("click", onDone);
          return;
        }
        reason = check.reason;
      }
    } catch {
      reason = "device-error";
    }
    root.innerHTML = `<div class="result bad"><div class="badge">${ICON.cross}</div>
      <h1>Recado no guardado</h1>
      <p>${escapeHTML(RECEIVE_COPY[reason] ?? REASON_COPY[reason])}</p>
      <div class="foot"><button id="again">Escanear otra vez</button>
      <button id="cancel" class="ghost">Volver a mi identidad</button></div></div>`;
    root.querySelector("#again")!.addEventListener("click", () => void scan());
    root.querySelector("#cancel")!.addEventListener("click", onDone);
  };

  await scan();
  return () => stopCamera();
}
